import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Download, FileText, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { usersApi, ordersApi, invoicesApi } from '../../services/api';
import { PROSTHESIS_TYPE_LABELS, getSubtypeLabel } from '../../utils/constants';
import { formatOrderRef } from '../../utils/orderRef';

const euro = (n) =>
    Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

const workToDescription = (work) => {
    const typeLabel = PROSTHESIS_TYPE_LABELS[work.prosthesisType] || work.prosthesisType || 'Travail';
    const subtype = work.prosthesisSubtype ? ` - ${getSubtypeLabel(work.prosthesisType, work.prosthesisSubtype)}` : '';
    const teeth = work.toothNumbers?.length ? ` (dents ${work.toothNumbers.join(', ')})` : '';
    return `${typeLabel}${subtype}${teeth}`;
};

const downloadBlobFile = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
};

const MODEL_3D_UNIT_PRICE = '3'; // prix unitaire par défaut du modèle 3D, modifiable dans la facture

// Construit les groupes éditables (une ligne par travail, + une ligne "Modèle 3D"
// à 3 € sous chaque travail qui en demande un) à partir des commandes expédiées.
const buildGroups = (orders) =>
    orders.map((o) => ({
        order: o,
        included: true,
        items: (o.works || []).flatMap((w) => {
            const line = { description: workToDescription(w), quantity: 1, unitPrice: '' };
            return w.hasDigitalModel
                ? [line, { description: 'Modèle 3D', quantity: 1, unitPrice: MODEL_3D_UNIT_PRICE }]
                : [line];
        }),
    }));

const BillingModal = ({ onClose, onGenerated }) => {
    const [view, setView] = useState('list'); // 'list' | 'create'
    const [invoices, setInvoices] = useState([]);
    const [loadingList, setLoadingList] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState('');
    const [groups, setGroups] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [notes, setNotes] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);

    const loadInvoices = useCallback(() => {
        setLoadingList(true);
        invoicesApi.list()
            .then((data) => setInvoices(data || []))
            .catch(() => setError('Impossible de charger les factures.'))
            .finally(() => setLoadingList(false));
    }, []);

    useEffect(() => { loadInvoices(); }, [loadInvoices]);

    useEffect(() => {
        let active = true;
        usersApi.getClients()
            .then((data) => active && setClients(data || []))
            .catch(() => active && setError('Impossible de charger les clients.'));
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!clientId) { setGroups([]); return; }
        let active = true;
        setLoadingOrders(true);
        setError(null);
        ordersApi.getAll({ clientId, status: 'shipped' })
            .then((orders) => { if (active) setGroups(buildGroups(orders || [])); })
            .catch(() => active && setError('Impossible de charger les commandes.'))
            .finally(() => active && setLoadingOrders(false));
        return () => { active = false; };
    }, [clientId]);

    // Filtre par date (optionnel) sur la date de commande
    const orderDate = (o) => (o.orderDate || o.created_at || '').slice(0, 10);
    const passesDate = (g) => {
        const d = orderDate(g.order);
        if (dateFrom && d < dateFrom) return false;
        if (dateTo && d > dateTo) return false;
        return true;
    };
    const visibleGroups = groups.filter(passesDate);
    const includedGroups = visibleGroups.filter((g) => g.included);
    const total = includedGroups.reduce(
        (s, g) => s + g.items.reduce((gs, it) => gs + (Number(it.quantity) || 0) * (Number(it.unitPrice) || 0), 0),
        0
    );

    // Adresses de facturation divergentes parmi les commandes sélectionnées : la facture
    // n'utilise qu'une seule adresse (celle de la 1re commande) → on alerte le prothésiste.
    const billingKey = (o) => [
        o.billing_name, o.billing_address_line1, o.billing_address_line2,
        o.billing_postal_code, o.billing_city, o.billing_country, o.billing_siret, o.billing_vat_number,
    ].map((v) => (v || '').trim()).join('|');
    const billingMismatch = (() => {
        if (includedGroups.length < 2) return null;
        const refKey = billingKey(includedGroups[0].order);
        const divergent = includedGroups.slice(1).filter((g) => billingKey(g.order) !== refKey);
        return divergent.length ? divergent.map((g) => formatOrderRef(g.order)) : null;
    })();

    const toggleGroup = (gi) =>
        setGroups((prev) => prev.map((g, i) => (i === gi ? { ...g, included: !g.included } : g)));
    const updateItem = (gi, ii, field, value) =>
        setGroups((prev) => prev.map((g, i) =>
            i === gi ? { ...g, items: g.items.map((it, j) => (j === ii ? { ...it, [field]: value } : it)) } : g));
    const addItem = (gi) =>
        setGroups((prev) => prev.map((g, i) =>
            i === gi ? { ...g, items: [...g.items, { description: '', quantity: 1, unitPrice: '' }] } : g));
    const removeItem = (gi, ii) =>
        setGroups((prev) => prev.map((g, i) =>
            i === gi ? { ...g, items: g.items.filter((_, j) => j !== ii) } : g));

    const handleGenerate = async () => {
        setError(null);
        if (includedGroups.length === 0) {
            setError('Sélectionnez au moins une commande.');
            return;
        }
        const payloadGroups = includedGroups.map((g) => ({
            orderId: g.order.id,
            orderRef: formatOrderRef(g.order),
            items: g.items.map((it) => ({
                description: it.description.trim(),
                quantity: Number(it.quantity),
                unitPrice: Number(it.unitPrice),
            })),
        }));
        for (const g of payloadGroups) {
            if (g.items.length === 0) { setError('Chaque commande doit avoir au moins une ligne.'); return; }
            if (g.items.some((it) => !it.description)) { setError('Chaque ligne doit avoir une désignation.'); return; }
            if (g.items.some((it) => !(it.quantity > 0))) { setError('Chaque ligne doit avoir une quantité positive.'); return; }
            if (g.items.some((it) => !(it.unitPrice >= 0) || it.unitPrice === '')) { setError('Chaque ligne doit avoir un prix unitaire.'); return; }
        }

        setSubmitting(true);
        try {
            const created = await invoicesApi.create({ clientId, groups: payloadGroups, notes: notes.trim() || undefined });
            setResult(created);
            if (onGenerated) onGenerated();
        } catch (e) {
            setError(e.message || 'Erreur lors de la génération de la facture.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownload = useCallback(async () => {
        if (!result) return;
        setDownloading(true);
        try {
            const blob = await invoicesApi.downloadBlob(result.id);
            downloadBlobFile(blob, `${result.invoiceNumber}.pdf`);
        } catch (e) {
            setError('Échec du téléchargement du PDF.');
        } finally {
            setDownloading(false);
        }
    }, [result]);

    const handleDownloadRow = async (inv) => {
        setDownloadingId(inv.id);
        try {
            const blob = await invoicesApi.downloadBlob(inv.id);
            downloadBlobFile(blob, `${inv.invoiceNumber}.pdf`);
        } catch (e) {
            setError('Échec du téléchargement du PDF.');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDeleteRow = async (inv) => {
        if (!window.confirm(
            `Annuler la facture ${inv.invoiceNumber} ?\n\nLa facture sera supprimée et ses ${inv.orderCount} commande${inv.orderCount > 1 ? 's repasseront' : ' repassera'} au statut « Expédiée ». Cette action est irréversible.`
        )) return;
        setDeletingId(inv.id);
        setError(null);
        try {
            await invoicesApi.remove(inv.id);
            setInvoices((prev) => prev.filter((i) => i.id !== inv.id));
            if (onGenerated) onGenerated();
        } catch (e) {
            setError('Échec de l\'annulation de la facture.');
        } finally {
            setDeletingId(null);
        }
    };

    const resetCreate = () => {
        setClientId(''); setGroups([]); setNotes(''); setDateFrom(''); setDateTo(''); setResult(null);
    };
    const goCreate = () => { setError(null); resetCreate(); setView('create'); };
    const backToList = () => { setError(null); resetCreate(); setView('list'); loadInvoices(); };

    return (
        <div className="fixed inset-0 overlay-frost flex items-center justify-center z-[70] animate-fade-in p-4">
            <div className="glass-deep border border-white/10 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-modal-in">
                <div className="flex items-center justify-between p-4 border-b border-white/10 sticky top-0 glass-deep z-10">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <FileText className="h-5 w-5 text-indigo-400" />
                        Facturation
                    </h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="h-5 w-5" /></button>
                </div>

                <div className="p-4 space-y-4">
                    {error && (
                        <div className="bg-red-500/10 border border-red-500/40 text-red-300 text-sm rounded-md px-3 py-2">{error}</div>
                    )}

                    {/* Vue liste des factures existantes */}
                    {view === 'list' && (
                        <>
                            <button
                                onClick={goCreate}
                                className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center justify-center gap-2"
                            >
                                <Plus className="h-4 w-4" /> Nouvelle facture
                            </button>

                            {loadingList ? (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement des factures…
                                </div>
                            ) : invoices.length === 0 ? (
                                <p className="text-sm text-gray-400 py-6 text-center">Aucune facture pour le moment.</p>
                            ) : (
                                <div className="divide-y divide-white/10 border border-white/10 rounded-lg overflow-hidden">
                                    {invoices.map((inv) => (
                                        <div key={inv.id} className="flex items-center justify-between gap-3 px-3 py-2.5 bg-black/20">
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-white truncate">
                                                    {inv.invoiceNumber} — {inv.clientName}
                                                </p>
                                                <p className="text-xs text-gray-400">
                                                    {new Date(inv.issueDate).toLocaleDateString('fr-FR')} · {inv.orderCount} commande{inv.orderCount > 1 ? 's' : ''} · {euro(inv.total)}
                                                </p>
                                            </div>
                                            <div className="flex-shrink-0 flex items-center gap-2">
                                                <button
                                                    onClick={() => handleDownloadRow(inv)}
                                                    disabled={downloadingId === inv.id}
                                                    className="px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-md flex items-center gap-1.5"
                                                >
                                                    {downloadingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                                    PDF
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteRow(inv)}
                                                    disabled={deletingId === inv.id}
                                                    title="Annuler la facture"
                                                    className="p-1.5 text-gray-400 hover:text-red-400 disabled:opacity-60"
                                                >
                                                    {deletingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* Vue création */}
                    {view === 'create' && (
                    <>
                    <button onClick={backToList} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
                        ← Retour à la liste
                    </button>

                    {/* Résultat : facture générée */}
                    {result ? (
                        <div className="space-y-4 py-4">
                            <div className="flex items-center gap-3 text-green-400">
                                <CheckCircle className="h-6 w-6" />
                                <div>
                                    <p className="font-semibold text-white">Facture {result.invoiceNumber} générée</p>
                                    <p className="text-sm text-gray-400">
                                        {result.orderCount} commande{result.orderCount > 1 ? 's' : ''} · total {euro(result.total)}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={handleDownload}
                                disabled={downloading}
                                className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-md flex items-center justify-center gap-2"
                            >
                                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                Télécharger le PDF
                            </button>
                            <button onClick={backToList} className="w-full px-4 py-2 bg-white/15 hover:bg-white/20 text-gray-200 rounded-md">
                                Retour à la liste
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Sélection client */}
                            <div>
                                <label className="block text-sm text-gray-400 mb-1">Client</label>
                                <select
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    className="w-full bg-black/30 border border-white/15 rounded px-2 py-2 text-sm text-white"
                                >
                                    <option value="">— Choisir un client —</option>
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}{c.clinic_name ? ` (${c.clinic_name})` : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtre par date (optionnel) sur la date de commande */}
                            {clientId && !loadingOrders && groups.length > 0 && (
                                <div className="flex flex-wrap items-end gap-3">
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Du (optionnel)</label>
                                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                                            className="bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white" />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Au (optionnel)</label>
                                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                            className="bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white" />
                                    </div>
                                    {(dateFrom || dateTo) && (
                                        <button onClick={() => { setDateFrom(''); setDateTo(''); }}
                                            className="text-xs text-gray-400 hover:text-white underline pb-2">Réinitialiser</button>
                                    )}
                                </div>
                            )}

                            {loadingOrders && (
                                <div className="flex items-center justify-center py-8 text-gray-400">
                                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement des commandes…
                                </div>
                            )}

                            {clientId && !loadingOrders && groups.length === 0 && (
                                <p className="text-sm text-gray-400 py-6 text-center">
                                    Aucune commande expédiée à facturer pour ce client.
                                </p>
                            )}

                            {clientId && !loadingOrders && groups.length > 0 && visibleGroups.length === 0 && (
                                <p className="text-sm text-gray-400 py-6 text-center">
                                    Aucune commande sur la période sélectionnée.
                                </p>
                            )}

                            {/* Commandes expédiées + lignes */}
                            {visibleGroups.map((g) => {
                                const gi = groups.indexOf(g);
                                return (
                                <div key={g.order.id} className={`border rounded-lg p-3 ${g.included ? 'border-indigo-500/50 bg-black/30' : 'border-white/10 bg-black/10 opacity-60'}`}>
                                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                                        <input type="checkbox" checked={g.included} onChange={() => toggleGroup(gi)} className="accent-indigo-500" />
                                        <span className="text-sm font-semibold text-white">
                                            Commande {formatOrderRef(g.order)}
                                            {g.order.patient_name_code ? ` — ${g.order.patient_name_code}` : ''}
                                        </span>
                                    </label>

                                    {g.included && (
                                        <div className="space-y-2 pl-1">
                                            {g.items.map((it, ii) => (
                                                <div key={ii} className="flex gap-2 items-start">
                                                    <input
                                                        type="text" value={it.description}
                                                        onChange={(e) => updateItem(gi, ii, 'description', e.target.value)}
                                                        placeholder="Désignation"
                                                        className="flex-1 bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white"
                                                    />
                                                    <input
                                                        type="number" min="1" value={it.quantity} title="Quantité"
                                                        onChange={(e) => updateItem(gi, ii, 'quantity', e.target.value)}
                                                        className="w-14 bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white text-right"
                                                    />
                                                    <input
                                                        type="number" min="0" step="0.01" value={it.unitPrice} placeholder="P.U." title="Prix unitaire HT"
                                                        onChange={(e) => updateItem(gi, ii, 'unitPrice', e.target.value)}
                                                        className="w-24 bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white text-right"
                                                    />
                                                    <button
                                                        onClick={() => removeItem(gi, ii)} disabled={g.items.length === 1}
                                                        className="p-1.5 text-gray-400 hover:text-red-400 disabled:opacity-30" title="Supprimer la ligne"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                            <button onClick={() => addItem(gi)} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
                                                <Plus className="h-3.5 w-3.5" /> Ajouter une ligne
                                            </button>
                                        </div>
                                    )}
                                </div>
                                );
                            })}

                            {billingMismatch && (
                                <div className="bg-amber-500/10 border border-amber-500/50 text-amber-300 text-sm rounded-md px-3 py-2 flex items-start gap-2">
                                    <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    <span>
                                        Adresse de facturation différente sur : <strong>{billingMismatch.join(', ')}</strong>.
                                        La facture utilisera l'adresse de la 1<sup>re</sup> commande sélectionnée — vérifiez, ou décochez ces commandes et facturez-les à part.
                                    </span>
                                </div>
                            )}

                            {includedGroups.length > 0 && (
                                <>
                                    <textarea
                                        value={notes} onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Notes (optionnel)" rows={2}
                                        className="w-full bg-black/30 border border-white/15 rounded px-2 py-1.5 text-sm text-white"
                                    />
                                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                                        <span className="text-sm text-gray-400">
                                            {includedGroups.length} commande{includedGroups.length > 1 ? 's' : ''} · Total à payer
                                        </span>
                                        <span className="text-lg font-semibold text-white">{euro(total)}</span>
                                    </div>
                                    <button
                                        onClick={handleGenerate} disabled={submitting}
                                        className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-md flex items-center justify-center gap-2"
                                    >
                                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                                        Générer la facture
                                    </button>
                                </>
                            )}
                        </>
                    )}
                    </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BillingModal;
